import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Typography, FormControlLabel, Checkbox, FormGroup, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PrintIcon from '@mui/icons-material/Print';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Send, Close, crossIcon } from '../../../assets/images/index';
import LoaderSvg from '../../../assets/images/loader.svg';
import Button from '../../../components/button';
import Dropdown from '../../../components/dropdown/index';
import MathExpressionNew from '../../../components/common/mathjax';
import { useLessonStore } from '../../../utils/lessonStore';
import { SearchExerciseService } from '../../../services/search-exercise/search-exercise.service';
import { LessonService } from '../../../services/lessons/lesson.service';
import { showErrorToast, showSuccessToast, showInfoToast } from '../../../components/Toaster/index';
import { LessonPreviewUI } from '../../../components/lesson-preview';
import ExerciseDetailsDrawer from '../../../components/exercise-details-drawer/ExerciseDetailsDrawer';
import './lessons-builder.scss';

const LessonsBuilder: React.FC = () => {
  const { t } = useTranslation();
  const searchExerciseService = new SearchExerciseService();
  const lessonService = new LessonService();

  const {
    topic_id,
    level_id,
    sub_topic_id,
    instructions,
    steps,
    setTopicId,
    setLevelId,
    setSubTopicId,
    setInstructions,
    setSteps,
    resetForm,
    addStep,
    updateStep,
    removeStep,
    clearState,
    topicOptions: storeTopicOptions,
    isTopicsLoading,
    fetchTopics: storeFetchTopics,
    subTopicOptions: storeSubTopicOptions,
    setSubTopicOptions: storeSetSubTopicOptions,
    isSubTopicsLoading,
    fetchSubTopics: storeFetchSubTopics,
    levelOptions: storeLevelOptions,
    isLevelsLoading,
    fetchLevels: storeFetchLevels,
  } = useLessonStore();

  const [savingSection, setSavingSection] = useState<{ stepNum: number; section: string } | null>(null);
  const [generatingForStep, setGeneratingForStep] = useState<{ stepNum: number; section: string } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [previewFilter, setPreviewFilter] = useState<{ stepNum: number; section: 'teacher_content' | 'quiz_question' | 'teacher_summary' } | null>(null);
  const [loadingSection, setLoadingSection] = useState<{ stepNum: number; section: string } | null>(null);
  const [dbLoadedStepNums, setDbLoadedStepNums] = useState<Set<number>>(new Set());
  const [savedSections, setSavedSections] = useState<Record<number, Set<string>>>({})
  const [isLessonContentLoading, setIsLessonContentLoading] = useState(false);
  const [shareResult, setShareResult] = useState<{ token: string; pin: string } | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isPreparingPrint, setIsPreparingPrint] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [phaseModalOpen, setPhaseModalOpen] = useState(false);
  const [phaseExercises, setPhaseExercises] = useState<{ id: number; name: string }[]>([]);
  const [selectedPhaseExercises, setSelectedPhaseExercises] = useState<number[]>([]);
  const [pendingStepNum, setPendingStepNum] = useState<number | null>(null);
  const [isFetchingExercises, setIsFetchingExercises] = useState(false);
  const [isSavingPhase, setIsSavingPhase] = useState(false);
  const [isManualPhaseOpen, setIsManualPhaseOpen] = useState(false);
  const [previewExerciseId, setPreviewExerciseId] = useState<number | null>(null);
  const [savedPhases, setSavedPhases] = useState<Record<number, number[]>>({});
  const [isDeletingPhase, setIsDeletingPhase] = useState<number | null>(null);

  // Phase Modal Infinite Scroll, Search & Tab States
  const [phaseExercisePage, setPhaseExercisePage] = useState(1);
  const [hasMorePhaseExercises, setHasMorePhaseExercises] = useState(false);
  const [isLoadingMorePhaseExercises, setIsLoadingMorePhaseExercises] = useState(false);
  const [totalPhaseExercisesCount, setTotalPhaseExercisesCount] = useState(0);
  const [phaseExerciseTab, setPhaseExerciseTab] = useState<'all' | 'selected'>('all');
  const [phaseSearchTerm, setPhaseSearchTerm] = useState('');
  const [exerciseNameMap, setExerciseNameMap] = useState<Record<number, string>>({});
  const phaseListRef = useRef<HTMLDivElement | null>(null);
  const phaseSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activePhaseSearchRef = useRef<string>('');

  // AI chart reference image states
  const [chartImageBase64, setChartImageBase64] = useState<Record<number, string>>({});

  // AI chat states per step, using a nested dictionary structure for inputs
  const [aiInputs, setAiInputs] = useState<Record<number, Record<string, string>>>({});
  // Hidden file input references per step
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  // Track selected tool per step: 'content' | 'chart' | 'manual_chart'
  const [activeTools, setActiveTools] = useState<Record<number, 'content' | 'chart' | 'manual_chart'>>({});
  // Track tool dropdown visibility per step
  const [openDropdowns, setOpenDropdowns] = useState<Record<number, boolean>>({});
  const stepRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Autonomous Lesson Generator States (KAN-340)
  const [autogenModalOpen, setAutogenModalOpen] = useState(false);
  const [autogenMasterKey, setAutogenMasterKey] = useState('');
  const [autogenKeyError, setAutogenKeyError] = useState<string | null>(null);
  const [isAutogenStarting, setIsAutogenStarting] = useState(false);
  const [autogenJobId, setAutogenJobId] = useState<string | null>(null);
  const [autogenJobState, setAutogenJobState] = useState<{
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    stage_key?: string;
    stage_params?: Record<string, any>;
    progress_message?: string;
    sub_topics_progress?: Array<{
      name: string;
      status: string;
      stage_key?: string;
      stage_params?: Record<string, any>;
      message?: string;
      step_count?: number;
    }>;
    results?: Array<{
      sub_topic_name: string;
      sub_topic_id: number;
      status: string;
      step_count: number;
      lesson_id?: number;
      error?: string;
    }>;
    summary?: Record<string, number>;
    error?: string;
  } | null>(null);
  const autogenPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const resolveStageMessage = (stageKey?: string, stageParams?: Record<string, any>, fallback?: string) => {
    if (stageKey) {
      return t(`autoGenStage_${stageKey}`, stageParams || {});
    }
    return fallback || '';
  };

  useEffect(() => {
    if (!autogenJobId || autogenJobState?.status === 'completed' || autogenJobState?.status === 'failed') {
      if (autogenPollingIntervalRef.current) {
        clearInterval(autogenPollingIntervalRef.current);
        autogenPollingIntervalRef.current = null;
      }
      return;
    }

    const pollStatus = async () => {
      try {
        const statusData = await lessonService.getLessonAutogenStatus(autogenJobId);
        setAutogenJobState(statusData);

        if (statusData.status === 'completed' || statusData.status === 'failed') {
          if (autogenPollingIntervalRef.current) {
            clearInterval(autogenPollingIntervalRef.current);
            autogenPollingIntervalRef.current = null;
          }
          if (statusData.status === 'completed') {
            showSuccessToast(t('autoGenCompleted'));
          } else {
            showErrorToast(statusData.error || t('autoGenFailed'));
          }
        }
      } catch (err: any) {
        console.error('Error polling autogen status:', err);
      }
    };

    pollStatus();
    autogenPollingIntervalRef.current = setInterval(pollStatus, 2000);

    return () => {
      if (autogenPollingIntervalRef.current) {
        clearInterval(autogenPollingIntervalRef.current);
        autogenPollingIntervalRef.current = null;
      }
    };
  }, [autogenJobId, autogenJobState?.status, t]);

  const handleStartAutogen = async () => {
    if (!autogenMasterKey.trim()) {
      showInfoToast(t('autoGenEnterMasterKey'));
      setAutogenKeyError(t('autoGenEnterMasterKey'));
      return;
    }
    if (!topic_id || !level_id) {
      showErrorToast(t('autoGenTooltipSelectFirst'));
      return;
    }

    setIsAutogenStarting(true);
    setAutogenKeyError(null);
    setAutogenJobState(null);
    try {
      const res = await lessonService.startLessonAutogen({
        level_id,
        topic_id,
        sub_topic_id: sub_topic_id && sub_topic_id !== -1 ? sub_topic_id : undefined,
        master_key: autogenMasterKey.trim(),
      });
      setAutogenJobId(res.job_id);
      setAutogenJobState({
        status: 'pending',
        stage_key: 'job_init',
        progress_message: t('autoGenStage_job_init'),
      });
    } catch (err: any) {
      console.error('Failed to start lesson autogen:', err);
      if (err.response?.status === 403 || err.response?.status === 401) {
        setAutogenKeyError(t('autoGenInvalidMasterKey'));
        showErrorToast(t('autoGenInvalidMasterKey'));
      } else {
        showErrorToast(err.response?.data?.detail || t('autoGenFailed'));
      }
    } finally {
      setIsAutogenStarting(false);
    }
  };

  const handleCloseAutogenModal = async () => {
    const wasCompleted = autogenJobState?.status === 'completed';
    const completedResults = autogenJobState?.results;
    setAutogenModalOpen(false);
    setAutogenJobId(null);
    setAutogenJobState(null);
    setAutogenKeyError(null);

    if (wasCompleted && topic_id) {
      // 1. Refresh sub-topics dropdown options from DB so new sub-topics appear
      await storeFetchSubTopics(searchExerciseService, topic_id, level_id || undefined);

      // 2. If no sub-topic was selected (or was -1), auto-select the first generated/existing sub-topic
      let currentSubTopicId = sub_topic_id && sub_topic_id !== -1 ? sub_topic_id : undefined;
      if (!currentSubTopicId && completedResults && completedResults.length > 0) {
        const firstValid = completedResults.find(
          (r) => r.sub_topic_id && r.status?.toLowerCase() !== 'failed'
        ) || completedResults[0];
        if (firstValid?.sub_topic_id) {
          setSubTopicId(firstValid.sub_topic_id);
          return;
        }
      }

      // 3. Reload lesson content for the selected level and sub-topic
      if (level_id) {
        loadLessonData(topic_id, level_id, currentSubTopicId);
      }
    }
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const resizeImageForAi = (dataUrl: string, maxDim: number = 1024): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width <= maxDim && height <= maxDim) {
          resolve(dataUrl);
          return;
        }
        if (width > height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } else {
          resolve(dataUrl);
        }
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  };

  useEffect(() => {
    // Scroll to the newly added step smoothly
    if (steps.length > 0) {
      const latestStep = steps[steps.length - 1];
      const targetElement = stepRefs.current[latestStep.step_number];

      if (targetElement) {
        // Use timeout to ensure DOM has updated sizes before scrolling
        setTimeout(() => {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [steps.length]);

  const isGenerating = (stepNum: number, section: string) =>
    generatingForStep?.stepNum === stepNum && generatingForStep?.section === section;

  const renderSectionLoader = (stepNum: number, section: string) => {
    if (!isGenerating(stepNum, section)) return null;
    return (
      <div className="section-loading-overlay">
        <div className="loader-content-wrapper">
          <img src={LoaderSvg} alt="Loading" className="loader-icon" />
          <div className="loader-text">
            {activeTools[stepNum] === 'manual_chart'
              ? t('uploadingManualChart')
              : activeTools[stepNum] === 'chart'
                ? t('generatingChartWithAI')
                : t('generatingContentWithAI')}
          </div>
        </div>
      </div>
    );
  };

  const isStepValid = (step: any) => {
    const hasTitle = !!step.title?.trim();
    const hasTeacherContent = !!step.teacher_content?.trim();
    return hasTitle && hasTeacherContent;
  };

  useEffect(() => {
    clearState();
    storeFetchTopics(searchExerciseService);
    storeFetchLevels(searchExerciseService);

    return () => {
      clearState();
    };
  }, []);

  useEffect(() => {
    if (topic_id && level_id) {
      loadLessonData(topic_id, level_id, sub_topic_id || undefined);
    } else {
      resetForm();
    }
  }, [topic_id, level_id, sub_topic_id]);

  useEffect(() => {
    if (topic_id) {
      storeFetchSubTopics(searchExerciseService, topic_id, level_id || undefined);
    } else {
      storeSetSubTopicOptions([]);
    }
  }, [topic_id, level_id]);

  useEffect(() => {
    if (sub_topic_id && storeSubTopicOptions.length > 0) {
      const exists = storeSubTopicOptions.some((opt) => opt.value === sub_topic_id.toString());
      if (!exists && sub_topic_id !== -1) {
        setSubTopicId(null);
      }
    }
  }, [storeSubTopicOptions, sub_topic_id]);

  const loadLessonData = async (id: number, lvlId: number, subTopicId?: number) => {
    setIsLessonContentLoading(true);
    try {
      const data = await lessonService.getLessonByTopic(id, lvlId, subTopicId);
      if (data) {
        setInstructions(data.instructions || '');

        // Clear any lingering AI draft text / images from the previous topic
        setAiInputs({});
        setChartImageBase64({});
        setActiveTools({});

        if (data.content && data.content.length > 0) {
          setSteps(data.content);
          setDbLoadedStepNums(new Set(data.content.map((s: any) => s.step_number)));
        } else {
          resetForm();
          setDbLoadedStepNums(new Set());
        }

        if (data.phases) {
          const phasesMap: Record<number, number[]> = {};
          data.phases.forEach((p: any) => {
            phasesMap[p.step_num] = p.exercise_list;
          });
          setSavedPhases(phasesMap);
        }
      } else {
        resetForm();
        setDbLoadedStepNums(new Set());
      }
    } catch (error) {
      console.error("Failed to load lesson data", error);
      resetForm();
      setDbLoadedStepNums(new Set());
    } finally {
      setIsLessonContentLoading(false);
    }
  };

  const topicOptions = React.useMemo(() => [{ value: '', label: t('all') }, ...storeTopicOptions], [storeTopicOptions, t]);
  const subTopicOptions = React.useMemo(() => [{ value: '', label: t('choose') }, ...storeSubTopicOptions], [storeSubTopicOptions, t]);
  const levelOptions = React.useMemo(() => [{ value: '', label: t('all') }, ...storeLevelOptions], [storeLevelOptions, t]);

  const handleSectionClickCapture = (e: React.MouseEvent, stepNum: number) => {
    const currentStep = steps.find((s) => s.step_number === stepNum);
    if (!currentStep?.title?.trim()) {
      showInfoToast(t('pleaseEnterStepTitle'));
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleSaveLessonProgress = async (stepNum?: number, section?: string) => {
    if (!topic_id || !level_id) {
      showErrorToast(t('pleaseSelectTopicAndLevel') || 'Please select Topic and Level first');
      return;
    }

    if (stepNum !== undefined && section && section !== 'general') {
      const currentStep = steps.find((s) => s.step_number === stepNum);
      if (!currentStep?.title?.trim()) {
        showInfoToast(t('pleaseEnterStepTitle'));
        return;
      }
      setSavingSection({ stepNum, section });
    } else {
      setSavingSection({ stepNum: -1, section: 'general' });
    }

    try {
      await lessonService.saveLesson({
        topic_id,
        level_id,
        ...(sub_topic_id && sub_topic_id !== -1 ? { sub_topic_id } : {}),
        instructions: instructions,
        content: steps,
        is_active: false,
      });
      if (stepNum !== undefined && section) {
        setSavedSections((prev) => {
          const existing = new Set(prev[stepNum] || []);
          existing.add(section);
          return { ...prev, [stepNum]: existing };
        });
      }
      showSuccessToast(t('lessonSavedSuccessfully'));
    } catch (error) {
      showErrorToast(t('errorSavingLesson'));
    } finally {
      setSavingSection(null);
    }
  };

  const handleAiInputUpdate = (stepNum: number, section: string, val: string) => {
    setAiInputs((prev) => ({
      ...prev,
      [stepNum]: {
        ...(prev[stepNum] || {}),
        [section]: val,
      },
    }));
  };

  const handleGenerateContent = async (stepNum: number, section: string) => {
    const currentStep = steps.find((s) => s.step_number === stepNum);
    if (!currentStep?.title?.trim()) {
      showInfoToast(t('pleaseEnterStepTitle'));
      return;
    }

    const prompt = aiInputs[stepNum]?.[section] || '';
    if (!prompt.trim()) return;

    setGeneratingForStep({ stepNum, section });
    try {
      const currentStep = steps.find((s) => s.step_number === stepNum);
      const context = currentStep?.teacher_content || '';
      const rawImage = chartImageBase64[stepNum];
      const aiImage = rawImage ? await resizeImageForAi(rawImage) : undefined;

      const newContent = await lessonService.generateContentWithAI(
        prompt, context, topic_id || undefined, level_id || undefined, sub_topic_id || undefined, section, currentStep?.title, aiImage
      );

      // Update the specific section in the store
      switch (section) {
        case 'teacher_content':
          updateStep(stepNum, { teacher_content: newContent });
          break;
        case 'quiz_question': {
          let parsedQuiz = currentStep?.quiz;
          try {
            const cleaned = newContent.replace(/```json/gi, '').replace(/```/g, '').trim();
            const parsed = JSON.parse(cleaned);
            parsedQuiz = parsed.quiz ? parsed.quiz : parsed;
          } catch (e) { console.warn('Could not parse quiz AI output', e); }

          updateStep(stepNum, { quiz: parsedQuiz });
          break;
        }
        case 'teacher_summary':
          updateStep(stepNum, { teacher_summary: newContent });
          break;
      }

      // Do not clear AI input so the user can see their last prompt
      // handleAiInputUpdate(stepNum, section, '');
      showSuccessToast(t('contentUpdatedSuccessfully'));
      setChartImageBase64(prev => {
        const next = { ...prev };
        delete next[stepNum];
        return next;
      });
      if (fileInputRefs.current[stepNum]) {
        fileInputRefs.current[stepNum].value = "";
      }
    } catch (error) {
      showErrorToast(t('errorGeneratingAIContent'));
    } finally {
      setGeneratingForStep(null);
    }
  };

  const handleGenerateChart = async (stepNum: number) => {
    if (!topic_id || !level_id) {
      showErrorToast(t('pleaseSelectTopicAndLevel') || 'Please select Topic and Level first');
      return;
    }
    const prompt = aiInputs[stepNum]?.teacher_content;
    if (!prompt?.trim()) {
      showErrorToast(t('pleaseEnterAiInstruction'));
      return;
    }

    setGeneratingForStep({ stepNum, section: 'teacher_content' });
    try {
      const currentStep = steps.find((s) => s.step_number === stepNum);
      const teacher_content = currentStep?.teacher_content;
      const image_base64 = chartImageBase64[stepNum];
      const existing_chart_url = currentStep?.chart_url;
      const aiImage = image_base64 ? await resizeImageForAi(image_base64) : undefined;

      const chartUrl = await lessonService.generateChartWithAI(prompt, topic_id, level_id, sub_topic_id || undefined, stepNum, teacher_content, aiImage, existing_chart_url);
      updateStep(stepNum, { chart_url: chartUrl });

      // Clear the image base64 after a successful generation so it doesn't linger
      setChartImageBase64(prev => {
        const next = { ...prev };
        delete next[stepNum];
        return next;
      });
      if (fileInputRefs.current[stepNum]) {
        fileInputRefs.current[stepNum].value = "";
      }

      showSuccessToast(t('chartGeneratedSuccessfully'));
    } catch (error) {
      showErrorToast(t('failedToGenerateChart'));
    } finally {
      setGeneratingForStep(null);
    }
  };

  const handleManualChartUpload = async (stepNum: number) => {
    const image_base64 = chartImageBase64[stepNum];
    if (!image_base64) {
      showErrorToast(t('pleaseUploadImageFirst'));
      return;
    }
    if (!topic_id || !level_id) {
      showErrorToast(t('pleaseSelectTopicAndLevel') || 'Please select Topic and Level first');
      return;
    }

    setGeneratingForStep({ stepNum, section: 'teacher_content' });
    try {
      const chartUrl = await lessonService.uploadManualChart(topic_id, level_id, sub_topic_id || undefined, stepNum, image_base64);
      updateStep(stepNum, { chart_url: chartUrl });

      setChartImageBase64(prev => {
        const next = { ...prev };
        delete next[stepNum];
        return next;
      });
      if (fileInputRefs.current[stepNum]) {
        fileInputRefs.current[stepNum].value = "";
      }
      showSuccessToast(t('contentUpdatedSuccessfully'));
    } catch (error) {
      showErrorToast(t('errorUploadingImage'));
    } finally {
      setGeneratingForStep(null);
    }
  };

  const handleLoadSectionContentFromDB = async (stepNum: number, section: 'teacher_content' | 'quiz_question' | 'teacher_summary') => {
    if (!topic_id || !level_id) return;
    setLoadingSection({ stepNum, section });
    try {
      const data = await lessonService.getStepSectionContent(topic_id, level_id, stepNum, section, sub_topic_id || undefined);
      if (data === null || data === undefined) return;
      switch (section) {
        case 'teacher_content':
          updateStep(stepNum, { teacher_content: data as string });
          break;
        case 'quiz_question':
          updateStep(stepNum, { quiz: data as any });
          break;
        case 'teacher_summary':
          updateStep(stepNum, { teacher_summary: data as string });
          break;
      }
    } catch {
      showErrorToast(t('errorLoadingSection'));
    } finally {
      setLoadingSection(null);
    }
  };

  const handleShareLesson = async (regenerate: boolean = false) => {
    if (!topic_id || !level_id) {
      showErrorToast(t('pleaseSelectTopicAndLevel') || 'Please select Topic and Level first');
      return;
    }
    setIsSharing(true);
    try {
      const response = await lessonService.shareLesson(topic_id, level_id, sub_topic_id || undefined, regenerate);
      setShareResult({ token: response.share_token, pin: response.share_pin });
      setShareDialogOpen(true);
    } catch (e) {
      showErrorToast(t('errorSharingLesson'));
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyShareLink = () => {
    if (!shareResult) return;
    const url = `${window.location.origin}/shared-lesson/${shareResult.token}`;
    const textToCopy = `${t('hereIsTheLessonLink')}:\n${url}\n\n${t('thePinIs')}: ${shareResult.pin}`;
    navigator.clipboard.writeText(textToCopy);
    showSuccessToast(t('linkCopied'));
  };

  const handlePrintPdf = async () => {
    setIsPreparingPrint(true);
    try {
      // 1. Brief pause to let any newly mounted DOM nodes settle
      await new Promise((resolve) => setTimeout(resolve, 150));

      // 2. Await MathJax typesetPromise for all formulas in the preview modal
      if (window.MathJax?.typesetPromise) {
        const modalEl = document.querySelector('.preview-modal-content');
        if (modalEl) {
          await window.MathJax.typesetPromise([modalEl]);
        } else {
          await window.MathJax.typesetPromise();
        }
      }

      // 3. Ensure fonts are fully rendered
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      // 4. Short breathing space to finalize SVG rendering
      await new Promise((resolve) => setTimeout(resolve, 200));
    } catch (err) {
      console.warn('MathJax print preparation error:', err);
    } finally {
      setIsPreparingPrint(false);
    }

    const originalTitle = document.title;
    const topicLabel = topicOptions.find((opt) => opt.value === topic_id?.toString())?.label;
    const levelLabel = levelOptions.find((opt) => opt.value === level_id?.toString())?.label;
    if (topicLabel) {
      document.title = `${t('lesson')} - ${topicLabel}${levelLabel ? ` - ${levelLabel}` : ''}`;
    }

    window.print();

    setTimeout(() => {
      document.title = originalTitle;
    }, 1500);
  };

  const PHASE_PAGE_SIZE = 30;

  const fetchPhaseExercises = async (
    pageToFetch: number,
    searchTerm: string = '',
    isReset: boolean = false
  ) => {
    if (!topic_id || !level_id) return;

    if (isReset) {
      setIsFetchingExercises(true);
    } else {
      setIsLoadingMorePhaseExercises(true);
    }

    try {
      const response = await searchExerciseService.searchExerciseInDB(
        pageToFetch,
        PHASE_PAGE_SIZE,
        searchTerm ? searchTerm.trim() : undefined,
        undefined,
        level_id.toString(),
        topic_id.toString(),
        undefined,
        true
      );

      if (response && response.results) {
        const newExercises = response.results.map((ex: any) => ({
          id: ex.id,
          name: ex.filename ? ex.filename.replace('.docx', '') : `${t('exercise')} ${ex.id}`
        }));

        setExerciseNameMap(prev => {
          const next = { ...prev };
          newExercises.forEach((item: { id: number; name: string }) => {
            next[item.id] = item.name;
          });
          return next;
        });

        setPhaseExercises(prev => {
          if (isReset) return newExercises;
          const existingIds = new Set(prev.map(item => item.id));
          const uniqueNew = newExercises.filter((item: { id: number; name: string }) => !existingIds.has(item.id));
          return [...prev, ...uniqueNew];
        });

        const total = response.total || 0;
        setTotalPhaseExercisesCount(total);
        setPhaseExercisePage(pageToFetch);

        const currentTotalLoaded = isReset ? newExercises.length : (pageToFetch - 1) * PHASE_PAGE_SIZE + newExercises.length;
        setHasMorePhaseExercises(currentTotalLoaded < total && newExercises.length === PHASE_PAGE_SIZE);
      }
    } catch (error) {
      console.error("Failed to fetch exercises for phase stop", error);
    } finally {
      setIsFetchingExercises(false);
      setIsLoadingMorePhaseExercises(false);
    }
  };

  const handleOpenPhaseModal = async (stepNum: number, isManual: boolean = false) => {
    setIsManualPhaseOpen(isManual);
    setPendingStepNum(stepNum);
    const existingSelected = savedPhases[stepNum] || [];
    setSelectedPhaseExercises(existingSelected);
    setPhaseExercises([]);
    setPhaseExerciseTab('all');
    setPhaseSearchTerm('');
    activePhaseSearchRef.current = '';
    setPhaseExercisePage(1);
    setHasMorePhaseExercises(false);
    setPhaseModalOpen(true);

    fetchPhaseExercises(1, '', true);
  };

  const handlePhaseListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (phaseExerciseTab !== 'all') return;
    const target = e.currentTarget;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 30;
    if (nearBottom && hasMorePhaseExercises && !isLoadingMorePhaseExercises && !isFetchingExercises) {
      fetchPhaseExercises(phaseExercisePage + 1, activePhaseSearchRef.current, false);
    }
  };

  const handlePhaseSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhaseSearchTerm(value);

    if (phaseSearchTimeoutRef.current) {
      clearTimeout(phaseSearchTimeoutRef.current);
    }

    phaseSearchTimeoutRef.current = setTimeout(() => {
      const trimmed = value.trim();
      if (trimmed.length >= 3) {
        activePhaseSearchRef.current = trimmed;
        fetchPhaseExercises(1, trimmed, true);
      } else if (activePhaseSearchRef.current !== '') {
        // Query dropped below 3 characters after a previous search -> reset to full list
        activePhaseSearchRef.current = '';
        fetchPhaseExercises(1, '', true);
      }
    }, 300);
  };

  const handleConfirmPhase = async (savePhase: boolean) => {
    if (savePhase && pendingStepNum !== null && topic_id && level_id) {
      setIsSavingPhase(true);
      try {
        const selectedList = selectedPhaseExercises;

        await lessonService.saveLessonPhase({
          topic_id,
          level_id,
          sub_topic_id: sub_topic_id || -1,
          step_num: pendingStepNum,
          exercise_list: selectedList
        });
        setSavedPhases(prev => ({ ...prev, [pendingStepNum]: selectedList }));
      } catch (error) {
        console.error("Failed to save lesson phase", error);
        showErrorToast(t('errorSavingPhaseStop'));
      } finally {
        setIsSavingPhase(false);
      }
    }

    if (!isManualPhaseOpen) {
      addStep();
    }

    setPhaseModalOpen(false);
    setPendingStepNum(null);
    setIsManualPhaseOpen(false);
  };

  const selectedExercisesList = useMemo(() => {
    return selectedPhaseExercises.map(id => ({
      id,
      name: exerciseNameMap[id] || `${t('exercise')} ${id}`
    }));
  }, [selectedPhaseExercises, exerciseNameMap, t]);

  const handleDeletePhase = async (stepNum: number) => {
    if (!topic_id || !level_id) return;
    setIsDeletingPhase(stepNum);
    try {
      await lessonService.deleteLessonPhase(topic_id, level_id, stepNum, sub_topic_id || -1);
      setSavedPhases(prev => {
        const next = { ...prev };
        delete next[stepNum];
        return next;
      });
      showSuccessToast(t('phaseStopDeleted'));
    } catch (error) {
      showErrorToast(t('errorDeletingPhaseStop'));
    } finally {
      setIsDeletingPhase(null);
    }
  };

  const toggleExerciseSelection = (id: number) => {
    setSelectedPhaseExercises(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  return (
    <div className="lessons-builder-container">
      {/* Top Controls Row */}
      <Box className="top-controls-row">
        <div className="topic-dropdown-wrapper">
          <Dropdown
            label={t('selectLevel')}
            value={level_id?.toString() || ''}
            options={levelOptions}
            loading={isLevelsLoading}
            onChange={(val) => setLevelId(val ? Number(val) : null)}
          />
          <Dropdown
            label={t('selectTopic')}
            value={topic_id?.toString() || ''}
            options={topicOptions}
            loading={isTopicsLoading}
            onChange={(val) => setTopicId(val ? Number(val) : null)}
          />
          <Dropdown
            label={t('selectSubTopic')}
            value={sub_topic_id?.toString() || '-1'}
            options={subTopicOptions}
            loading={isSubTopicsLoading}
            onChange={(val) => setSubTopicId(val ? Number(val) : -1)}
            disabled={!topic_id}
          />
        </div>
        <div className="action-buttons">
          <Button
            text={t('autoGenLesson')}
            color="purple"
            disabled={!topic_id || !level_id || autogenJobState?.status === 'in_progress' || autogenJobState?.status === 'pending'}
            icon={<AutoAwesomeIcon fontSize="small" />}
            className="autogen-action-btn"
            onClick={() => {
              if (!topic_id || !level_id) {
                showInfoToast(t('autoGenTooltipSelectFirst'));
                return;
              }
              setAutogenModalOpen(true);
            }}
          />
          <Button
            text={t('generalLessonInstructions')}
            color="black"
            disabled={!topic_id}
            onClick={() => setInstructionsOpen(true)}
          />
          <Button
            text={t('teacherPreview')}
            color="black"
            disabled={!topic_id}
            icon={<VisibilityIcon fontSize="small" />}
            onClick={() => { setPreviewFilter(null); setPreviewOpen(true); }}
          />
        </div>
      </Box>

      {/* Main Form Content */}
      <div className={`lesson-steps-container ${isLessonContentLoading ? 'loading' : ''}`}>
        {isLessonContentLoading && (
          <div className="full-container-loader">
            <div className="loader-content-wrapper">
              <img src={LoaderSvg} alt="Loading..." className="loader-icon" />
              <span className="loader-text">{t('loadingLessonData')}</span>
            </div>
          </div>
        )}
        {steps.map((step) => (
          <Paper
            key={step.step_number}
            className="step-card"
            elevation={2}
            ref={(el) => (stepRefs.current[step.step_number] = el as any)}
          >
            {/* Step Card Label */}
            <div className="step-card-label">{t('createStepContent')} {step.step_number}</div>

            {/* Step Header */}
            <div className="step-header">
              <div className="step-badge">{step.step_number}</div>
              <input
                className="step-title-input"
                placeholder={t('stepTitlePlaceholder')}
                value={step.title || ''}
                disabled={!topic_id}
                onChange={(e) => updateStep(step.step_number, { title: e.target.value })}
              />
              <div className="stop-point-actions">
                <Button
                  text={savedPhases[step.step_number] ? t('editStopPoint') : t('markStopPoint')}
                  onClick={() => handleOpenPhaseModal(step.step_number, true)}
                  size="small"
                  color={savedPhases[step.step_number] ? "green" : "black"}
                  className="stop-point-btn"
                />
                {savedPhases[step.step_number] && (
                  <Button
                    text={t('removeStopPoint')}
                    onClick={() => handleDeletePhase(step.step_number)}
                    size="small"
                    color="red"
                    className="remove-stop-point-btn"
                    loader={isDeletingPhase === step.step_number}
                  />
                )}
              </div>
            </div>

            {/* Section 1: Teacher's Part */}
            <div className="section teacher-section" onClickCapture={(e) => handleSectionClickCapture(e, step.step_number)}>
              {renderSectionLoader(step.step_number, 'teacher_content')}
              {!step.title?.trim() && <div className="section-click-mask" />}
              <div className="section-top-row">
                <h4 className="section-title">{t('teacherPartTitle')}</h4>
                <button
                  type="button"
                  className="section-preview-link"
                  disabled={!step.teacher_content?.trim()}
                  onClick={() => { setPreviewFilter({ stepNum: step.step_number, section: 'teacher_content' }); setPreviewOpen(true); }}
                >
                  {t('sectionPreview')}
                </button>
              </div>
              <textarea
                className="content-textarea"
                value={step.teacher_content || ''}
                onChange={(e) => updateStep(step.step_number, { teacher_content: e.target.value })}
                placeholder={t('teacherPartPlaceholder')}
                rows={12}
                disabled={!step.title?.trim()}
              />

              {step.chart_url && (
                <div className="chart-preview-container">
                  <h5>{t('chartPreview')}</h5>
                  <div className="chart-preview">
                    <img src={step.chart_url} alt={t('generatedChartAlt')} className="generated-chart-img" />
                    <button
                      className="remove-chart-btn"
                      onClick={() => updateStep(step.step_number, { chart_url: '' })}
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
              <div className="ai-interaction-bar">
                <div className="ai-input-wrapper">
                  {chartImageBase64[step.step_number] && (
                    <div className="attached-image-preview">
                      <img src={chartImageBase64[step.step_number]} alt="Attached reference" />
                      <button
                        className="remove-image-btn"
                        onClick={() => {
                          const newImages = { ...chartImageBase64 };
                          delete newImages[step.step_number];
                          setChartImageBase64(newImages);
                          const el = fileInputRefs.current[step.step_number];
                          if (el) {
                            el.value = "";
                          }
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <textarea
                    rows={3}
                    placeholder={
                      activeTools[step.step_number] === 'chart'
                        ? t('chartPromptPlaceholder')
                        : activeTools[step.step_number] === 'manual_chart'
                          ? t('manualChartDisabledPlaceholder')
                          : t('aiInstructionUpdateContent')
                    }
                    value={aiInputs[step.step_number]?.teacher_content || ''}
                    disabled={!step.title?.trim() || activeTools[step.step_number] === 'manual_chart'}

                    onChange={(e) => handleAiInputUpdate(step.step_number, 'teacher_content', e.target.value)}
                  />
                  <div className="ai-input-footer">
                    <div className="ai-tool-dropdown-wrapper">
                      <div className="ai-tool-dropdown-container">
                        <button
                          type="button"
                          className="tools-dropdown-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdowns((prev) => ({
                              ...prev,
                              [step.step_number]: !prev[step.step_number],
                            }));
                          }}
                        >
                          <span className="tools-icon">🛠️</span>
                          {activeTools[step.step_number] === 'chart'
                            ? t('chartGeneration')
                            : activeTools[step.step_number] === 'manual_chart'
                              ? t('manualChartUpload')
                              : t('contentGeneration')}
                          <span className="dropdown-arrow">▼</span>
                        </button>

                        {openDropdowns[step.step_number] && (
                          <div className="tools-dropdown-menu">
                            <button
                              type="button"
                              className={`dropdown-item ${activeTools[step.step_number] !== 'chart' && activeTools[step.step_number] !== 'manual_chart' ? 'active' : ''}`}
                              onClick={() => {
                                handleAiInputUpdate(step.step_number, 'teacher_content', '');
                                setActiveTools({ ...activeTools, [step.step_number]: 'content' });
                                setOpenDropdowns({ ...openDropdowns, [step.step_number]: false });
                              }}
                            >
                              {t('contentGeneration')}
                              {activeTools[step.step_number] !== 'chart' && activeTools[step.step_number] !== 'manual_chart' && <span className="check-mark">✓</span>}
                            </button>
                            <button
                              type="button"
                              className={`dropdown-item ${activeTools[step.step_number] === 'chart' ? 'active' : ''}`}
                              onClick={() => {
                                handleAiInputUpdate(step.step_number, 'teacher_content', '');
                                setActiveTools({ ...activeTools, [step.step_number]: 'chart' });
                                setOpenDropdowns({ ...openDropdowns, [step.step_number]: false });
                              }}
                            >
                              {t('chartGeneration')}
                              {activeTools[step.step_number] === 'chart' && <span className="check-mark">✓</span>}
                            </button>
                            <button
                              type="button"
                              className={`dropdown-item ${activeTools[step.step_number] === 'manual_chart' ? 'active' : ''}`}
                              onClick={() => {
                                handleAiInputUpdate(step.step_number, 'teacher_content', '');
                                setActiveTools({ ...activeTools, [step.step_number]: 'manual_chart' });
                                setOpenDropdowns({ ...openDropdowns, [step.step_number]: false });
                              }}
                            >
                              {t('manualChartUpload')}
                              {activeTools[step.step_number] === 'manual_chart' && <span className="check-mark">✓</span>}
                            </button>
                          </div>
                        )}
                      </div>
                      {(activeTools[step.step_number] === 'manual_chart' || (activeTools[step.step_number] === 'chart' && !step.chart_url) || !activeTools[step.step_number] || activeTools[step.step_number] === 'content') && (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden-file-input"
                            ref={(el) => (fileInputRefs.current[step.step_number] = el)}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                if (file.size > 15 * 1024 * 1024) {
                                  showErrorToast(t('fileSizeExceedsLimit'));
                                  e.target.value = "";
                                  return;
                                }
                                try {
                                  const base64 = await readFileAsBase64(file);
                                  setChartImageBase64((prev) => ({
                                    ...prev,
                                    [step.step_number]: base64,
                                  }));
                                } finally {
                                  // Always clear value so change event triggers next time even for same file
                                  e.target.value = "";
                                }
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => fileInputRefs.current[step.step_number]?.click()}
                            title={chartImageBase64[step.step_number] ? t('imageAttached') : (activeTools[step.step_number] === 'manual_chart' ? t('manualChartUpload') : t('attachReferenceImage'))}
                          >
                            <AttachFileIcon color={chartImageBase64[step.step_number] ? "primary" : "inherit"} />
                          </IconButton>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      className={`chat-footer-form-btn ${generatingForStep?.stepNum === step.step_number && generatingForStep?.section === 'teacher_content' ? "disabled" : ""}`}
                      disabled={generatingForStep?.stepNum === step.step_number && generatingForStep?.section === 'teacher_content'}
                      onClick={() => {
                        if (activeTools[step.step_number] === 'chart') {
                          handleGenerateChart(step.step_number);
                        } else if (activeTools[step.step_number] === 'manual_chart') {
                          handleManualChartUpload(step.step_number);
                        } else {
                          handleGenerateContent(step.step_number, 'teacher_content');
                        }
                      }}
                    >
                      <img src={Send} alt="Send" />
                    </button>
                  </div>
                </div>
                <div className="section-actions-container">
                  <div className="action-buttons-row">
                    <Button color="green" text={t('confirmSectionContent')} loader={savingSection?.stepNum === step.step_number && savingSection?.section === 'teacher_content'} onClick={() => handleSaveLessonProgress(step.step_number, 'teacher_content')} />
                    <Button color="blue" text={t('loadFromDB')} loader={loadingSection?.stepNum === step.step_number && loadingSection?.section === 'teacher_content'} disabled={!topic_id || !step.title?.trim()} onClick={() => handleLoadSectionContentFromDB(step.step_number, 'teacher_content')} />
                  </div>
                  <Button
                    color="black"
                    text={t('stepPreview')}
                    disabled={!step.teacher_content?.trim()}
                    onClick={() => { setPreviewFilter({ stepNum: step.step_number, section: 'teacher_content' }); setPreviewOpen(true); }}
                  />
                </div>
              </div>


            </div>

            {/* Section 2: Check Question */}
            <div className="section check-question-section" onClickCapture={(e) => handleSectionClickCapture(e, step.step_number)}>
              {renderSectionLoader(step.step_number, 'quiz_question')}
              {!step.title?.trim() && <div className="section-click-mask" />}
              <div className="section-top-row">
                <h4 className="section-title">{t('checkQuestionTitle')}</h4>
                <button
                  type="button"
                  className="section-preview-link"
                  disabled={!step.quiz?.question?.trim()}
                  onClick={() => { setPreviewFilter({ stepNum: step.step_number, section: 'quiz_question' }); setPreviewOpen(true); }}
                >
                  {t('sectionPreview')}
                </button>
              </div>
              <textarea
                className="content-textarea"
                placeholder={t('checkQuestionPlaceholder')}
                value={step.quiz?.question || ''}
                disabled={!step.title?.trim()}
                onChange={(e) => updateStep(step.step_number, { quiz: { ...step.quiz, question: e.target.value } })}
                rows={3}
              />
              <div className="quiz-options">
                {[{ key: 'a', label: t('optionA') }, { key: 'b', label: t('optionB') }, { key: 'c', label: t('optionC') }].map(({ key, label }) => {
                  const isCorrect = step.quiz?.correct_option === key;
                  const value = step.quiz?.options?.[key as 'a' | 'b' | 'c'] || '';
                  return (
                    <div className="quiz-option-row" key={key}>
                      <span className="option-letter">{label}.</span>
                      <input
                        className="content-textarea quiz-option-input"
                        value={value}
                        disabled={!step.title?.trim()}
                        onChange={(e) => updateStep(step.step_number, { quiz: { ...step.quiz, options: { ...step.quiz?.options, [key]: e.target.value } } })}
                      />
                      <button
                        type="button"
                        className={`correct-option-btn ${isCorrect ? 'selected' : ''}`}
                        onClick={() => updateStep(step.step_number, { quiz: { ...step.quiz, correct_option: key } })}
                        disabled={!step.title?.trim()}
                        title={t('setAsCorrectAnswer')}
                      >
                        ✓
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="ai-interaction-bar">
                <div className="ai-input-wrapper">
                  <textarea
                    rows={3}
                    placeholder={t('aiInstructionChangeQuestion')}
                    value={aiInputs[step.step_number]?.quiz_question || ''}
                    disabled={!step.title?.trim()}
                    onChange={(e) => handleAiInputUpdate(step.step_number, 'quiz_question', e.target.value)}
                  />
                  <div className="ai-input-footer">
                    <div className="spacer" />
                    <button
                      type="button"
                      className={`chat-footer-form-btn ${generatingForStep?.stepNum === step.step_number && generatingForStep?.section === 'quiz_question' ? "disabled" : ""}`}
                      disabled={generatingForStep?.stepNum === step.step_number && generatingForStep?.section === 'quiz_question'}
                      onClick={() => handleGenerateContent(step.step_number, 'quiz_question')}
                    >
                      <img src={Send} alt="Send" />
                    </button>
                  </div>
                </div>
                <div className="section-actions-container">
                  <div className="action-buttons-row">
                    <Button color="green" text={t('confirmSectionContent')} loader={savingSection?.stepNum === step.step_number && savingSection?.section === 'quiz_question'} onClick={() => handleSaveLessonProgress(step.step_number, 'quiz_question')} />
                    <Button color="blue" text={t('loadFromDB')} loader={loadingSection?.stepNum === step.step_number && loadingSection?.section === 'quiz_question'} disabled={!topic_id || !step.title?.trim()} onClick={() => handleLoadSectionContentFromDB(step.step_number, 'quiz_question')} />
                  </div>
                  <Button
                    color="black"
                    text={t('stepPreview')}
                    disabled={!step.quiz?.question?.trim()}
                    onClick={() => { setPreviewFilter({ stepNum: step.step_number, section: 'quiz_question' }); setPreviewOpen(true); }}
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Step Summary */}
            <div className="section summary-section" onClickCapture={(e) => handleSectionClickCapture(e, step.step_number)}>
              {renderSectionLoader(step.step_number, 'teacher_summary')}
              {!step.title?.trim() && <div className="section-click-mask" />}
              <div className="section-top-row">
                <h4 className="section-title">{t('stepSummaryTitle')}</h4>
                <button
                  type="button"
                  className="section-preview-link"
                  disabled={!step.teacher_summary?.trim()}
                  onClick={() => { setPreviewFilter({ stepNum: step.step_number, section: 'teacher_summary' }); setPreviewOpen(true); }}
                >
                  {t('sectionPreview')}
                </button>
              </div>
              <textarea
                className="content-textarea"
                placeholder={t('stepSummaryPlaceholder')}
                value={step.teacher_summary || ''}
                disabled={!step.title?.trim()}
                onChange={(e) => updateStep(step.step_number, { teacher_summary: e.target.value })}
                rows={8}
              />
              <div className="ai-interaction-bar">
                <div className="ai-input-wrapper">
                  <textarea
                    rows={3}
                    placeholder={t('aiInstructionRefineSummary')}
                    value={aiInputs[step.step_number]?.teacher_summary || ''}
                    disabled={!step.title?.trim()}
                    onChange={(e) => handleAiInputUpdate(step.step_number, 'teacher_summary', e.target.value)}
                  />
                  <div className="ai-input-footer">
                    <div className="spacer" />
                    <button
                      type="button"
                      className={`chat-footer-form-btn ${generatingForStep?.stepNum === step.step_number && generatingForStep?.section === 'teacher_summary' ? "disabled" : ""}`}
                      disabled={generatingForStep?.stepNum === step.step_number && generatingForStep?.section === 'teacher_summary'}
                      onClick={() => handleGenerateContent(step.step_number, 'teacher_summary')}
                    >
                      <img src={Send} alt="Send" />
                    </button>
                  </div>
                </div>
                <div className="section-actions-container">
                  <div className="action-buttons-row">
                    <Button color="green" text={t('confirmSectionContent')} loader={savingSection?.stepNum === step.step_number && savingSection?.section === 'teacher_summary'} onClick={() => handleSaveLessonProgress(step.step_number, 'teacher_summary')} />
                    <Button color="blue" text={t('loadFromDB')} loader={loadingSection?.stepNum === step.step_number && loadingSection?.section === 'teacher_summary'} disabled={!topic_id || !step.title?.trim()} onClick={() => handleLoadSectionContentFromDB(step.step_number, 'teacher_summary')} />
                  </div>
                  <Button
                    color="black"
                    text={t('stepPreview')}
                    disabled={!step.teacher_summary?.trim()}
                    onClick={() => { setPreviewFilter({ stepNum: step.step_number, section: 'teacher_summary' }); setPreviewOpen(true); }}
                  />
                </div>
              </div>
            </div>

            {/* Bottom step action — visible for new steps or the last step in the list */}
            {(!dbLoadedStepNums.has(step.step_number) || step.step_number === Math.max(...steps.map((s) => s.step_number))) && (
              <div className="finish-step-action">
                <Button
                  text={t('createNewStep')}
                  color="red"
                  disabled={
                    !topic_id ||
                    !isStepValid(step) ||
                    (!dbLoadedStepNums.has(step.step_number) && !savedSections[step.step_number]?.has('teacher_content'))
                  }
                  onClick={() => addStep()}
                  className="finish-step-btn"
                />
              </div>
            )}
          </Paper>
        ))}
      </div>

      <Dialog
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); }}
        maxWidth="md"
        fullWidth
        className="lesson-preview-dialog"
      >
        <DialogTitle className="preview-dialog-title">
          <div className="preview-dialog-header-actions">
            <span>
              {previewFilter !== null
                ? `${t('sectionPreview')} — ${t('STEP')} ${previewFilter.stepNum}`
                : `${t('teacherPreview')}${topic_id ? ` - ${t('lesson')} ${topicOptions.find((opt) => opt.value === topic_id?.toString())?.label || ''}` : ''}`
              }
            </span>
            <IconButton
              onClick={() => { setPreviewOpen(false); }}
              size="small"
              className="preview-close-icon"
            >
              <img src={Close} alt="Close" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <div className="preview-modal-content">
            <LessonPreviewUI
              steps={steps as any}
              previewFilter={previewFilter}
              topicName={topicOptions.find((opt) => opt.value === topic_id?.toString())?.label || ''}
              subTopicName={subTopicOptions.find((opt) => opt.value === sub_topic_id?.toString())?.label || ''}
              levelName={levelOptions.find((opt) => opt.value === level_id?.toString())?.label || ''}
            />
          </div>
        </DialogContent>
        <DialogActions className="preview-dialog-actions">
          {previewFilter === null && topic_id && (
            <>
              <Button
                text={t('downloadPdf')}
                color="green"
                icon={<PrintIcon />}
                onClick={handlePrintPdf}
                loader={isPreparingPrint}
                disabled={isPreparingPrint || (dbLoadedStepNums.size === 0 && Object.keys(savedSections).length === 0)}
              />
              <Button
                text={t('shareLesson')}
                color="blue"
                onClick={() => handleShareLesson(false)}
                loader={isSharing}
                disabled={dbLoadedStepNums.size === 0 && Object.keys(savedSections).length === 0}
              />
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="preview-dialog-title">
          <div className="preview-dialog-header-actions">
            <span>{t('shareLesson')}</span>
            <IconButton
              onClick={() => { setShareDialogOpen(false); }}
              size="small"
              className="preview-close-icon"            >
              <img src={Close} alt="Close" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {shareResult && (
            <div className="share-info-box">
              <Typography variant="subtitle2">{t('shareUrl')}</Typography>
              <Typography variant="body1" className="share-text-box">{`${window.location.origin}/shared-lesson/${shareResult.token}`}</Typography>
              <Typography variant="subtitle2" className="share-pin-label">{t('sharePin')}</Typography>
              <Typography variant="h5" className="share-text-box share-pin-box">{shareResult.pin}</Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button text={t('regenerateLink')} color="red" onClick={() => handleShareLesson(true)} loader={isSharing} />
          <Button text={t('copyLink')} color="green" onClick={handleCopyShareLink} />
        </DialogActions>
      </Dialog>

      {/* General Instructions Modal */}
      <Dialog open={instructionsOpen} onClose={() => setInstructionsOpen(false)} maxWidth="md" fullWidth PaperProps={{ className: 'instructions-dialog-paper' }}>
        <DialogTitle className="preview-dialog-title">
          {t('generalLessonInstructions')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" className="instructions-description">
            {t('generalInstructionsDescription')}
          </Typography>
          <textarea
            className="content-textarea instructions-textarea"
            placeholder={t('enterLessonInstructionsPlaceholder')}
            value={instructions || ''}
            onChange={(e) => setInstructions(e.target.value)}
            rows={20}
          />
        </DialogContent>
        <DialogActions>
          <Button text={t('cancel')} color="black" outline onClick={() => setInstructionsOpen(false)} />
          <Button text={t('confirm')} color="blue" onClick={() => {
            setInstructionsOpen(false);
            handleSaveLessonProgress(-1, 'general');
          }} loader={savingSection?.section === 'general'} />
        </DialogActions>
      </Dialog>

      {/* Lesson Phase Stop Point Modal */}
      <Dialog
        open={phaseModalOpen}
        onClose={() => setPhaseModalOpen(false)}
        maxWidth="md"
        fullWidth
        className="phase-stop-modal"
      >
        <DialogTitle className="preview-dialog-title">
          <div className="preview-dialog-header-actions">
            <span>{t('phaseStopPointTitle')}</span>
            <IconButton onClick={() => setPhaseModalOpen(false)} size="small" className="preview-close-icon">
              <img src={Close} alt="Close" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" className="phase-stop-question">
            {t('phaseStopPointQuestion')}
          </Typography>

          <Box className="phase-exercises-section">
            <Typography variant="subtitle2" className="exercises-title">
              {t('selectExercisesToSuggest')}
            </Typography>

            <Box className="phase-tabs-row">
              <button
                type="button"
                className={`phase-tab-btn ${phaseExerciseTab === 'all' ? 'active' : ''}`}
                onClick={() => setPhaseExerciseTab('all')}
              >
                {t('allExercisesCount', { count: totalPhaseExercisesCount })}
              </button>
              <button
                type="button"
                className={`phase-tab-btn ${phaseExerciseTab === 'selected' ? 'active' : ''}`}
                onClick={() => setPhaseExerciseTab('selected')}
              >
                {t('selectedExercisesCount', { count: selectedPhaseExercises.length })}
              </button>
            </Box>

            {phaseExerciseTab === 'all' && (
              <Box className="phase-search-box">
                <TextField
                  size="small"
                  placeholder={t('searchExercisePlaceholder')}
                  value={phaseSearchTerm}
                  onChange={handlePhaseSearchChange}
                  className="phase-search-input"
                  fullWidth
                  variant="outlined"
                />
              </Box>
            )}

            {phaseExerciseTab === 'all' ? (
              isFetchingExercises ? (
                <Box className="loader-box"><CircularProgress size={24} /></Box>
              ) : phaseExercises.length > 0 ? (
                <Box
                  className="exercises-list-vertical"
                  ref={phaseListRef}
                  onScroll={handlePhaseListScroll}
                >
                  {phaseExercises.map((ex) => (
                    <FormControlLabel
                      key={ex.id}
                      control={
                        <Checkbox
                          checked={selectedPhaseExercises.includes(ex.id)}
                          onChange={() => toggleExerciseSelection(ex.id)}
                          size="small"
                        />
                      }
                      label={
                        <Box component="span" className="exercise-link-wrapper">
                          <Typography
                            variant="body2"
                            component="span"
                            className="exercise-id-link"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setPreviewExerciseId(ex.id);
                            }}
                          >
                            {ex.id}
                          </Typography>
                          <Typography variant="body2" component="span">
                            - {ex.name}
                          </Typography>
                        </Box>
                      }
                      className="exercise-item-label"
                    />
                  ))}
                  {isLoadingMorePhaseExercises && (
                    <Box className="loading-more-box">
                      <CircularProgress size={18} />
                      <Typography variant="caption" color="textSecondary">
                        {t('loadingMoreExercises')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary" className="no-exercises-msg">
                  {t('noExercisesFoundForThisTopic')}
                </Typography>
              )
            ) : (
              selectedExercisesList.length > 0 ? (
                <Box className="exercises-list-vertical">
                  {selectedExercisesList.map((ex) => (
                    <FormControlLabel
                      key={ex.id}
                      control={
                        <Checkbox
                          checked={true}
                          onChange={() => toggleExerciseSelection(ex.id)}
                          size="small"
                        />
                      }
                      label={
                        <Box component="span" className="exercise-link-wrapper">
                          <Typography
                            variant="body2"
                            component="span"
                            className="exercise-id-link"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setPreviewExerciseId(ex.id);
                            }}
                          >
                            {ex.id}
                          </Typography>
                          <Typography variant="body2" component="span">
                            - {ex.name}
                          </Typography>
                        </Box>
                      }
                      className="exercise-item-label"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary" className="no-exercises-msg">
                  {t('noExercisesSelected')}
                </Typography>
              )
            )}
          </Box>

          <Box className="phase-stop-choice-buttons">
            <Button
              text={t('yes')}
              color='green'
              onClick={() => handleConfirmPhase(true)}
              className="choice-btn"
              loader={isSavingPhase}
            />
            <Button
              text={t('no')}
              color='red'
              onClick={() => handleConfirmPhase(false)}
              className="choice-btn"
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Autonomous Lesson Generator Modal (KAN-340) */}
      <Dialog
        open={autogenModalOpen}
        onClose={
          autogenJobState?.status === 'in_progress' || autogenJobState?.status === 'pending'
            ? undefined
            : handleCloseAutogenModal
        }
        maxWidth="sm"
        fullWidth
        className="autogen-dialog"
      >
        <DialogTitle className="autogen-dialog-title">
          <Box className="autogen-header-title-box">
            <AutoAwesomeIcon className="autogen-header-icon" />
            <Typography variant="h6" component="span" className="autogen-title-text">
              {t('autoGenModalTitle')}
            </Typography>
          </Box>
          {autogenJobState?.status !== 'in_progress' && autogenJobState?.status !== 'pending' && (
            <IconButton onClick={handleCloseAutogenModal} size="small" className="preview-close-icon">
              <img src={Close} alt="Close" />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers className="autogen-dialog-content">
          {/* Target Parameters Summary */}
          <Box className="autogen-summary-box">
            <div className="autogen-summary-row">
              <span className="label">{t('autoGenLevelLabel')}</span>
              <span className="value">
                {storeLevelOptions.find((o) => o.value == level_id)?.label || t('autoGenUnitsSuffix', { count: level_id })}
              </span>
            </div>
            <div className="autogen-summary-row">
              <span className="label">{t('autoGenTopicLabel')}</span>
              <span className="value">
                {storeTopicOptions.find((o) => o.value == topic_id)?.label || topic_id}
              </span>
            </div>
            <div className="autogen-summary-row">
              <span className="label">{t('autoGenScopeLabel')}</span>
              <span className="value highlight">
                {sub_topic_id && sub_topic_id !== -1
                  ? t('autoGenSingleSubTopic', {
                      name: storeSubTopicOptions.find((o) => o.value == sub_topic_id)?.label || sub_topic_id,
                    })
                  : t('autoGenAllSubTopics')}
              </span>
            </div>
          </Box>

          {/* Rule 1 Guarantee Banner: Never overwrite existing lessons */}
          <Box className="autogen-safety-banner">
            <span className="safety-icon">🛡️</span>
            <Typography variant="body2">{t('autoGenSafeNotice')}</Typography>
          </Box>

          {/* Step 1: Input Master Key (if job not started or reset) */}
          {!autogenJobState && (
            <Box className="autogen-key-section">
              <Typography variant="subtitle2" className="autogen-key-title" gutterBottom>
                {t('autoGenEnterMasterKey')}
              </Typography>
              <TextField
                type="password"
                fullWidth
                size="small"
                placeholder={t('autoGenMasterKeyPlaceholder')}
                value={autogenMasterKey}
                error={!!autogenKeyError}
                helperText={autogenKeyError}
                onChange={(e) => {
                  setAutogenMasterKey(e.target.value);
                  if (autogenKeyError) setAutogenKeyError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleStartAutogen();
                  }
                }}
                autoFocus
              />
            </Box>
          )}

          {/* Step 2: Live Progress Indicator & Message */}
          {autogenJobState && (
            <Box className="autogen-progress-section">
              {(autogenJobState.status === 'pending' || autogenJobState.status === 'in_progress') && (
                <Box className="autogen-loading-row">
                  <CircularProgress size={28} className="autogen-spinner" />
                  <Typography variant="body1" className="autogen-loading-text">
                    {resolveStageMessage(
                      autogenJobState.stage_key,
                      autogenJobState.stage_params,
                      autogenJobState.progress_message
                    ) || t('autoGenGenerating')}
                  </Typography>
                </Box>
              )}

              {/* Progress per sub-topic */}
              {autogenJobState.sub_topics_progress && autogenJobState.sub_topics_progress.length > 0 && (
                <Box className="autogen-subtopics-list">
                  {autogenJobState.sub_topics_progress.map((st, idx) => {
                    const stStatus = (st.status || '').toLowerCase();
                    const isSkipped = stStatus === 'skipped_existing' || stStatus === 'skipped';
                    const isSuccess = stStatus === 'created' || stStatus === 'overwritten' || stStatus === 'saved';
                    const isFailed = stStatus === 'failed';

                    return (
                      <div key={idx} className={`subtopic-progress-item ${stStatus}`}>
                        <span className="st-icon">
                          {isSkipped ? '⏭️' : isSuccess ? '✅' : isFailed ? '❌' : '⏳'}
                        </span>
                        <span className="st-name">{st.name}</span>
                        <span className="st-msg">
                          {resolveStageMessage(st.stage_key, st.stage_params, st.message)}
                        </span>
                      </div>
                    );
                  })}
                </Box>
              )}

              {/* Completed Status */}
              {autogenJobState.status === 'completed' && (
                <Box className="autogen-result-box success">
                  <Typography variant="h6" className="autogen-success-title">
                    🎉 {t('autoGenCompleted')}
                  </Typography>
                  <Typography variant="body2" className="autogen-message-text">
                    {resolveStageMessage(
                      autogenJobState.stage_key,
                      autogenJobState.stage_params,
                      autogenJobState.progress_message
                    )}
                  </Typography>
                  {autogenJobState.results && autogenJobState.results.length > 0 && (
                    <Box className="autogen-results-list">
                      {autogenJobState.results.map((r, idx) => {
                        const rStatus = (r.status || '').toLowerCase();
                        const isSkipped = rStatus === 'skipped_existing' || rStatus === 'skipped';
                        const isCreated = rStatus === 'created' || rStatus === 'overwritten';

                        return (
                          <div key={idx} className={`result-item ${rStatus}`}>
                            <span>
                              {isSkipped ? '⏭️' : isCreated ? '✅' : '❌'}
                            </span>
                            <strong>{r.sub_topic_name}</strong>
                            <span className={`status-tag ${isSkipped ? 'skipped' : isCreated ? 'success' : 'failed'}`}>
                              {isSkipped
                                ? t('autoGenStatusSkipped', { count: r.step_count })
                                : isCreated
                                ? t('autoGenStatusCreated', { count: r.step_count })
                                : t('autoGenStatusFailed', { error: r.error || '' })}
                            </span>
                          </div>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {/* Failed Status */}
              {autogenJobState.status === 'failed' && (
                <Box className="autogen-result-box error">
                  <Typography variant="h6" className="autogen-error-title">
                    ⚠️ {t('autoGenFailed')}
                  </Typography>
                  <Typography variant="body2" className="autogen-message-text">
                    {autogenJobState.error ||
                      resolveStageMessage(
                        autogenJobState.stage_key,
                        autogenJobState.stage_params,
                        autogenJobState.progress_message
                      )}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions className="autogen-dialog-actions">
          {!autogenJobState ? (
            <>
              <Button text={t('cancel')} color="transparent" onClick={handleCloseAutogenModal} />
              <Button
                text={t('autoGenStartBtn')}
                color="purple"
                disabled={!autogenMasterKey.trim() || isAutogenStarting}
                loader={isAutogenStarting}
                onClick={handleStartAutogen}
              />
            </>
          ) : autogenJobState.status === 'completed' ? (
            <Button
              text={t('autoGenRefreshAndClose')}
              color="green"
              onClick={handleCloseAutogenModal}
            />
          ) : autogenJobState.status === 'failed' ? (
            <>
              <Button text={t('cancel')} color="transparent" onClick={handleCloseAutogenModal} />
              <Button
                text={t('TryAgain')}
                color="blue"
                onClick={() => {
                  setAutogenJobState(null);
                  setAutogenJobId(null);
                }}
              />
            </>
          ) : null}
        </DialogActions>
      </Dialog>

      <ExerciseDetailsDrawer
        open={previewExerciseId !== null}
        onClose={() => setPreviewExerciseId(null)}
        exerciseId={previewExerciseId}
      />
    </div>
  );
};

export default LessonsBuilder;
